/**
 * The billboard at the top of Home.
 *
 * ## Why the app had none
 *
 * Home was a flex column of rails and nothing above them, so the first thing the app said
 * on opening was "here are some rows". Every product this one sits beside opens with one
 * thing, large, that you can start watching in a single click — not because a big picture
 * is fashionable, but because the most common intent on opening a media app is *resume the
 * thing I was watching*, and a rail makes that a scan rather than a glance.
 *
 * ## What it features, and why it is not curated
 *
 * The first Continue Watching entry, when there is one. That is the honest answer to
 * "what does this household want right now" and it costs no new endpoint: Home already
 * fetches it. Failing that, the first item of the first row — which is the recommender's
 * top pick, because the server orders the rows that way.
 *
 * There is deliberately no editorial "featured" concept. This is one household's private
 * catalogue; there is nobody to do the editing, and a rotating spotlight picked at random
 * would be a worse answer than the one the viewer left half-finished.
 *
 * ## Artwork
 *
 * A real `backdrop` when enrichment has reached the item, and a cropped poster until it
 * has — which for most of a 55,000-film catalogue means "for a long time", because the
 * crawl is deliberately slow. Both paths are live at once and always will be, so neither
 * is a placeholder for the other.
 *
 * A 2:3 poster cropped to 21:9 keeps its middle band, which is usually the part with a
 * face in it: acceptable rather than good. Building the hero against `MediaImage` is what
 * made the upgrade one line here rather than a second component.
 *
 * The episode's own artwork is tried before the show's, then the show's before either
 * logo — an episode almost never carries a backdrop, and without the fallback the hero for
 * a resumed series would be the one card on the page with no picture.
 *
 * Behind and around it sits the item's own tint, the same hash `MediaImage` uses for
 * placeholders, so an item with no artwork at all still opens on a coloured field rather
 * than on a black rectangle with text floating in it.
 */
import { Link } from 'react-router-dom';
import type { CatalogItemDto } from '@filmodasi/shared';
import { Icon } from './Icon';
import { MediaImage, tintFor } from './MediaImage';
import { maturityLabel } from '../catalog/maturity';
import { FavoriteButton } from './FavoriteButton';
import { PlayOnTvButton } from './PlayOnTvButton';

export interface HeroProps {
  /** What plays. For a series this is the *episode*, matching Continue Watching. */
  item: CatalogItemDto;
  /** The show, when `item` is one of its episodes. Its title leads. */
  series?: CatalogItemDto | null;
  /** Present only for something already started; drives the bar and the verb. */
  percent?: number | null;
  /** True when this is the next unwatched episode rather than a resumable position. */
  nextEpisode?: boolean;
}

function runtime(sec: number | null): string | null {
  if (!sec || sec < 60) return null;
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} dk`;
  return `${Math.floor(m / 60)} sa ${m % 60} dk`;
}

function episodeLabel(item: CatalogItemDto): string | null {
  if (item.seasonNumber == null || item.episodeNumber == null) return null;
  const s = String(item.seasonNumber).padStart(2, '0');
  const e = String(item.episodeNumber).padStart(2, '0');
  return `S${s}B${e}`;
}

export function Hero({ item, series = null, percent = null, nextEpisode = false }: HeroProps) {
  const headline = series?.title ?? item.title;
  /*
   * The artwork falls back through the show's, because an episode almost never carries its
   * own. Without this the hero for a resumed series is the one card on the page with no
   * picture — the opposite of what a billboard is for.
   */
  const art =
    item.backdrop ??
    series?.backdrop ??
    item.poster ??
    series?.poster ??
    item.logo ??
    series?.logo;

  /* A series is browsed, not played. Everything else has a stream behind it. */
  const isSeries = item.type === 'series';
  const to = isSeries ? `/series/${item.id}` : `/watch/${item.id}`;

  const started = percent != null && percent > 0 && !nextEpisode;
  const label = isSeries ? 'İncele' : nextEpisode ? 'Sıradaki bölümü oynat' : started ? 'Devam et' : 'Oynat';

  const meta = [
    episodeLabel(item),
    item.year ? String(item.year) : null,
    runtime(item.durationSec),
    item.rating ? `★ ${item.rating.toFixed(1)}` : null,
    /*
     * The provider's own rating string first, the classifier's band only as a fallback.
     *
     * A real `PG-13` says more than "Gençler" does, and printing both would be the same
     * fact twice — the band was usually *derived from* the string. The band exists for the
     * majority of the catalogue where the provider sent no rating at all.
     */
    item.ageRating ?? maturityLabel(item.maturity),
  ].filter((x): x is string => Boolean(x));

  return (
    <section className="hero" style={{ backgroundImage: tintFor(item.id) }} aria-labelledby="hero-title">
      <div className="hero__media">
        {/*
          The one image on the page worth fetching eagerly: it is above the fold on every
          viewport, and it is the largest contentful paint. Everything below it stays lazy.
        */}
        <MediaImage src={art} shape="hero" seed={item.id} priority />
      </div>

      <div className="hero__body">
        <h1 className="hero__title" id="hero-title">
          {headline}
        </h1>

        {meta.length > 0 && (
          <p className="hero__meta">
            {meta.map((part, i) => (
              <span key={i}>{part}</span>
            ))}
          </p>
        )}

        {/* Enrichment fills this in slowly, so most items have none. No placeholder: an
            empty paragraph reserving space for text that may never arrive is worse than
            a shorter hero. */}
        {item.plot && <p className="hero__plot">{item.plot}</p>}

        {started && (
          <div className="hero__progress" role="presentation">
            <span style={{ width: `${Math.min(100, percent)}%` }} />
          </div>
        )}

        <div className="hero__actions">
          <Link to={to} className="hero__play">
            <Icon name={isSeries ? 'info' : 'play'} size={1.1} />
            {label}
          </Link>
          {/* The show, not the episode — the unit a viewer means by "favorite". */}
          <FavoriteButton item={series ?? item} />
          {!isSeries && <PlayOnTvButton itemId={item.id} />}
        </div>
      </div>
    </section>
  );
}
